<script>
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import Spinner from "@/components/ui/Spinner";
import ModalAdditionalInformation from "@/components/administration/ModalAdditionalInformation.vue";
import programPatientService from "@/services/programPatient";
import {
  getProgramStatusType,
  getProgramStatusText,
} from "@/services/program-status";
import programPatientStatusMixin from "@/mixins/programPatientStatus";

export default {
  name: "AdministrationHistory",
  components: {
    BaseSubNav,
    Spinner,
    ModalAdditionalInformation,
  },
  mixins: [programPatientStatusMixin],
  data() {
    return {
      isLoading: false,
      programPatients: [],
      searchInput: "",
      statusFilter: "",
      showAdditionalInfoModal: false,
      showJudgmentHistoryModal: false,
      selectedProgramPatient: null,
    };
  },
  computed: {
    filteredProgramPatients() {
      let filtered = this.programPatients;

      if (this.searchInput) {
        const query = this.searchInput.toLowerCase();
        filtered = filtered.filter((pp) => {
          const patient = pp.patient;
          const program = pp.program;
          return (
            patient?.firstName?.toLowerCase().includes(query) ||
            patient?.lastName?.toLowerCase().includes(query) ||
            program?.name?.toLowerCase().includes(query)
          );
        });
      }

      if (this.statusFilter) {
        filtered = filtered.filter((pp) => pp.status === this.statusFilter);
      }

      return filtered;
    },
    statusOptions() {
      return [
        { value: "", label: "Todos los estados" },
        { value: "active", label: "Activo" },
        { value: "completed", label: "Completado" },
        { value: "cancelled", label: "Cancelado" },
        { value: "rejected", label: "Rechazado" },
      ];
    },
  },
  watch: {
    "$route.name": {
      handler: function () {
        this.getData();
      },
      immediate: true,
    },
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const res = await programPatientService.getProgramPatients({
          status: "non-pending",
        });
        this.programPatients = res.data || res;
      } catch (error) {
        console.error("Error loading program patients:", error);
      } finally {
        this.isLoading = false;
      }
    },
    goToPatientDashboard(patient) {
      const patientId = typeof patient === "string" ? patient : patient._id;
      this.$router.push({
        name: "patient-dashboard",
        params: { id: patientId },
      });
    },
    getRemainingFormsTooltip(programPatient) {
      const forms = programPatient.program.initialData;
      if (forms.length <= 1) return "";

      const remainingForms = forms.slice(1).map((form) => form.name);
      return remainingForms.join(", ");
    },
    approveProgramPatient(programPatient) {
      this.changeProgramPatientStatus(programPatient, {
        title: "Aprobar programa",
        actionWord: "aprobar",
        status: "active",
        confirmButtonText: "Aprobar",
      });
    },
    rejectProgramPatient(programPatient) {
      this.changeProgramPatientStatus(programPatient, {
        title: "Rechazar programa",
        actionWord: "rechazar",
        status: "rejected",
        confirmButtonText: "Rechazar programa",
      });
    },
    cancelProgramPatient(programPatient) {
      this.changeProgramPatientStatus(programPatient, {
        title: "Cancelar programa",
        actionWord: "cancelar",
        status: "cancelled",
        confirmButtonText: "Cancelar programa",
      });
    },
    completeProgramPatient(programPatient) {
      this.changeProgramPatientStatus(programPatient, {
        title: "Finalizar programa",
        actionWord: "finalizar",
        status: "completed",
        confirmButtonText: "Finalizar programa",
      });
    },
    openAdditionalInfo(programPatient) {
      this.selectedProgramPatient = programPatient;
      this.showAdditionalInfoModal = true;
    },
    closeAdditionalInfo() {
      this.showAdditionalInfoModal = false;
      this.selectedProgramPatient = null;
    },
    submitAdditionalInfo(payload) {
      if (payload?.context?.programPatient) {
        const index = this.programPatients.findIndex(
          (p) => p._id === this.selectedProgramPatient._id
        );
        if (index !== -1) {
          this.$set(
            this.programPatients[index],
            "additionalInformation",
            payload.context.programPatient.additionalInformation
          );
        }
      }

      this.$message({ type: "success", message: "Solicitud enviada" });
      this.closeAdditionalInfo();
      this.$emit("additional-info-requested", payload);
    },
    askToCompleteAdditionalInformation(programPatient) {
      this.$confirm(
        "¿Ya han respondido la información solicitada?",
        "Completar información adicional",
        {
          confirmButtonText: "Sí",
          cancelButtonText: "No",
          type: "question",
        }
      )
        .then(() => {
          this.completeAdditionalInformation(programPatient);
        })
        .catch(() => {});
    },
    async completeAdditionalInformation(programPatient) {
      try {
        await programPatientService.completeAdditionalInformation(
          programPatient._id
        );

        const index = this.programPatients.findIndex(
          (p) => p._id === programPatient._id
        );
        if (index !== -1) {
          this.$set(this.programPatients[index], "additionalInformation", {
            ...this.programPatients[index].additionalInformation,
            status: "completed",
            statusChangedAt: new Date(),
            statusChangedBy: this.$store.state.user._id,
          });
        }

        this.$message({
          type: "success",
          message: "Información adicional marcada como completada",
        });
      } catch (error) {
        console.error("Error completing additional information:", error);
        this.$message({
          type: "error",
          message: "Error al completar la información adicional",
        });
      }
    },
    async updateProgramPatientStatus(programPatient, status, payload = {}) {
      try {
        const updatedProgramPatient =
          await programPatientService.updateProgramPatientStatus(
            programPatient._id,
            status,
            payload
          );

        const index = this.programPatients.findIndex(
          (p) => p._id === programPatient._id
        );
        if (index !== -1) {
          const existingPatient = this.programPatients[index].patient;
          const existingProgram = this.programPatients[index].program;

          const patientData =
            typeof updatedProgramPatient.patient === "object" &&
            updatedProgramPatient.patient !== null
              ? updatedProgramPatient.patient
              : existingPatient;

          const programData =
            typeof updatedProgramPatient.program === "object" &&
            updatedProgramPatient.program !== null
              ? updatedProgramPatient.program
              : existingProgram;

          const mergedData = {
            ...updatedProgramPatient,
            patient: patientData,
            program: programData,
          };

          this.$set(this.programPatients, index, mergedData);
        }

        const messages = {
          active: "Programa aprobado exitosamente",
          rejected: "Programa rechazado",
          cancelled: "Programa cancelado",
          completed: "Programa finalizado exitosamente",
        };

        const types = {
          active: "success",
          rejected: "warning",
          cancelled: "warning",
          completed: "success",
        };

        this.$message({
          type: types[status] || "success",
          message: messages[status] || "Estado actualizado",
        });
      } catch (error) {
        console.error("Error updating program patient status:", error);
        this.$message({
          type: "error",
          message: "Error al actualizar el estado del programa",
        });
      }
    },
    getStatusType: getProgramStatusType,
    getStatusText: getProgramStatusText,
    openJudgmentHistory(programPatient) {
      this.selectedProgramPatient = programPatient;
      this.showJudgmentHistoryModal = true;
    },
    closeJudgmentHistory() {
      this.showJudgmentHistoryModal = false;
      this.selectedProgramPatient = null;
    },
    hasJudgmentHistory(programPatient) {
      return (
        programPatient?.statusHistory &&
        Array.isArray(programPatient.statusHistory) &&
        programPatient.statusHistory.length > 0
      );
    },
  },
};
</script>

<template lang="pug">
div
  BaseSubNav(
    navigationType="administration"
    :showNavigation="true"
  )
    template(#title)
      | Historial de Programas

    template(#actions)
      el-select.status-filter(
        v-model="statusFilter"
        placeholder="Filtrar por estado"
        clearable
      )
        el-option(
          v-for="option in statusOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"
        )
      el-input.search__input(
        prefix-icon="el-icon-search"
        v-model="searchInput"
        placeholder="Buscar por paciente o programa"
        :clearable="true"
      )

  .box.box--with-subnav
    .box__content--stretch
      spinner(v-if="isLoading && !programPatients.length")

      h2.no-results(v-if="!isLoading && !programPatients.length") No hay programas en el historial

      h2.no-results(
        v-if="!isLoading && programPatients.length && !filteredProgramPatients.length"
      ) No se encontraron resultados

      table(v-if="programPatients.length && filteredProgramPatients.length")
        thead
          tr
            th Paciente
            th(style="max-width: 150px;") Programa
            th Estado
            th Formularios
            th Fecha de Creación
            th Fecha de Actualización
            th Acciones
        tbody
          tr(
            v-for="programPatient in filteredProgramPatients"
            :key="programPatient._id"
          )
            td(@click="goToPatientDashboard(programPatient.patient)") {{ programPatient.patient.lastName | capitalize }}, {{ programPatient.patient.firstName | capitalize }}
            td(@click="goToPatientDashboard(programPatient.patient)")
              span(
                v-tooltip="programPatient.program.name"
              ) {{ programPatient.program.name }}
            td(@click="goToPatientDashboard(programPatient.patient)")
              el-tag(
                :type="getStatusType(programPatient.status)"
                size="small"
              ) {{ getStatusText(programPatient.status) }}
              span.status-warning-icon(
                v-if="programPatient.additionalInformation && programPatient.additionalInformation.requested && programPatient.additionalInformation.status === 'pending'"
                @click.stop="askToCompleteAdditionalInformation(programPatient)"
                v-tooltip="programPatient.additionalInformation.description || 'Información adicional solicitada'"
              )
                micon(name="info")
              span.status-completed-icon(
                v-if="programPatient.additionalInformation && programPatient.additionalInformation.requested && programPatient.additionalInformation.status === 'completed'"
                v-tooltip="'Información adicional respondida correctamente'"
              )
                micon(name="check")
            td(@click="goToPatientDashboard(programPatient.patient)") 
              | {{ programPatient.program.initialData[0].name }} 
              span.span-more(
                v-if="programPatient.program.initialData.length > 1"
                v-tooltip="getRemainingFormsTooltip(programPatient)"
              ) +{{ programPatient.program.initialData.length - 1 }}
            td(@click="goToPatientDashboard(programPatient.patient)") {{ programPatient.createdAt | formatDateAndTime }}
            td(@click="goToPatientDashboard(programPatient.patient)") {{ programPatient.updatedStatusAt | formatDateAndTime }}
            td.actions
              .actions-container
                template(v-if="programPatient.status === 'rejected'")
                  button.button.button--action.success(
                    @click.stop="approveProgramPatient(programPatient)"
                    v-tooltip="'Aprobar'"
                  )
                    micon(name="check")
                  span(
                    v-tooltip="programPatient.additionalInformation && programPatient.additionalInformation.requested ? 'Ya se ha solicitado información' : 'Solicitar información adicional'"
                  )
                    button.button.button--action.additional-info(
                      @click.stop="openAdditionalInfo(programPatient)"
                      :disabled="programPatient.additionalInformation && programPatient.additionalInformation.requested"
                    )
                      micon(name="info")
                
                template(v-if="programPatient.status === 'active'")
                  button.button.button--action.danger-light(
                    @click.stop="completeProgramPatient(programPatient)"
                    v-tooltip="'Finalizar'"
                  )
                    micon(name="check")
                  button.button.button--action.danger(
                    @click.stop="cancelProgramPatient(programPatient)"
                    v-tooltip="'Cancelar'"
                  )
                    micon(name="clear")
                  span(
                    v-tooltip="programPatient.additionalInformation && programPatient.additionalInformation.requested ? 'Ya se ha solicitado información' : 'Solicitar información adicional'"
                  )
                    button.button.button--action.additional-info(
                      @click.stop="openAdditionalInfo(programPatient)"
                      :disabled="programPatient.additionalInformation && programPatient.additionalInformation.requested"
                    )
                      micon(name="info")
                
                template(v-if="programPatient.status === 'completed'")
                  button.button.button--action.success(
                    @click.stop="approveProgramPatient(programPatient)"
                    v-tooltip="'Reabrir'"
                  )
                    micon(name="refresh")
                  span(
                    v-tooltip="programPatient.additionalInformation && programPatient.additionalInformation.requested ? 'Ya se ha solicitado información' : 'Solicitar información adicional'"
                  )
                    button.button.button--action.additional-info(
                      @click.stop="openAdditionalInfo(programPatient)"
                      :disabled="programPatient.additionalInformation && programPatient.additionalInformation.requested"
                    )
                      micon(name="info")
                
                template(v-if="programPatient.status === 'cancelled'")
                  button.button.button--action.success(
                    @click.stop="approveProgramPatient(programPatient)"
                    v-tooltip="'Reabrir'"
                  )
                    micon(name="refresh")
                  span(
                    v-tooltip="programPatient.additionalInformation && programPatient.additionalInformation.requested ? 'Ya se ha solicitado información' : 'Solicitar información adicional'"
                  )
                    button.button.button--action.additional-info(
                      @click.stop="openAdditionalInfo(programPatient)"
                      :disabled="programPatient.additionalInformation && programPatient.additionalInformation.requested"
                    )
                      micon(name="info")
                
                button.button.button--action.info(
                  @click.stop="openJudgmentHistory(programPatient)"
                  v-tooltip="'Ver dictámenes'"
                  :disabled="!hasJudgmentHistory(programPatient)"
                )
                  micon(name="description")
  ModalAdditionalInformation(
    :showDialogModal="showAdditionalInfoModal"
    :programPatient="selectedProgramPatient"
    @close="closeAdditionalInfo"
    @submit="submitAdditionalInfo"
  )
  el-dialog(
    title="Historial de Dictámenes"
    :visible.sync="showJudgmentHistoryModal"
    width="700px"
    :before-close="closeJudgmentHistory"
  )
    div.judgment-history-container
      .judgment-item(
        v-for="(item, index) in selectedProgramPatient?.statusHistory"
        :key="item._id || index"
      )
        .judgment-header
          .judgment-status
            el-tag(
              :type="getStatusType(item.status)"
              size="small"
            ) {{ getStatusText(item.status) }}
          .judgment-date {{ item.createdAt | formatDateAndTime }}
        .judgment-text Hecho por: {{ item.updatedBy.firstName | capitalize }} {{ item.updatedBy.lastName | capitalize }}
        .judgment-text {{ item.judgment }}
</template>

<style lang="scss" scoped>
.box--with-subnav {
  min-height: 100px;
}

.no-results {
  text-align: center;
  padding: 50px;
  font-size: 1.5em;
  width: 100%;
}

tbody td:hover {
  cursor: pointer;
}

tbody td.actions {
  cursor: default;
}

tbody td:nth-child(2) {
  max-width: 150px;

  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.status-filter {
  min-width: 200px;
  margin-right: 10px;
}

.span-more {
  color: #fff;
  background-color: #040055;
  margin-left: 5px;
  padding: 6px;
  border-radius: 100%;
  font-size: 12px;
  cursor: pointer;
}

.actions-container {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;

  > span {
    display: inline-flex;
  }
}

.button--action .micon {
  width: 16px !important;
  height: 16px !important;
}

.button--action {
  border: none;
  padding: 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  min-height: 28px;

  &:disabled {
    background-color: #c0c4cc;
    cursor: not-allowed;
    opacity: 0.6;

    &:hover {
      background-color: #c0c4cc;
    }

    ::v-deep .micon {
      fill: #909399;
    }
  }

  &.success {
    background-color: #67c23a;

    &:hover {
      background-color: #5daf34;
    }

    ::v-deep .micon {
      fill: white;
    }
  }

  &.danger {
    background-color: #f56c6c;

    &:hover {
      background-color: #f45656;
    }

    ::v-deep .micon {
      fill: white;
    }
  }

  &.danger-light {
    background-color: #e74c3c;

    &:hover {
      background-color: #d43f2f;
    }

    ::v-deep .micon {
      fill: white;
    }
  }

  &.warning {
    background-color: #e6a23c;

    &:hover {
      background-color: #cf9236;
    }

    ::v-deep .micon {
      fill: white;
    }
  }

  &.info {
    background-color: #909399;

    &:hover {
      background-color: #82848a;
    }

    ::v-deep .micon {
      fill: white;
      width: 16px !important;
      height: 16px !important;
    }
  }

  &.primary {
    background-color: #409eff;

    &:hover {
      background-color: #3a8ee6;
    }

    ::v-deep .micon {
      fill: white;
    }
  }

  &.additional-info {
    background-color: #ffb300;

    &:hover {
      background-color: #ffa000;
    }

    ::v-deep .micon {
      fill: white;
    }
  }
}

.status-warning-icon .micon {
  margin-left: 6px;
  fill: #e6a23c;
  cursor: pointer;
}

.status-completed-icon .micon {
  margin-left: 6px;
  fill: #67c23a;
}

::v-deep .search__input {
  min-width: 300px;
}

::v-deep .el-message-box__status.el-icon-success,
::v-deep .el-message-box__status.el-icon-warning,
::v-deep .el-message-box__status.el-icon-info {
  display: none;
}

::v-deep .el-message-box__message {
  padding-left: 10px !important;
  padding-right: 10px !important;
}

.judgment-history-container {
  padding: 10px 0;
}

.judgment-item {
  padding: 15px;
  margin-bottom: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;

  .judgment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;

    .judgment-status {
      font-weight: 600;
    }

    .judgment-date {
      color: #909399;
      font-size: 12px;
    }
  }

  .judgment-text {
    color: #303133;
    line-height: 1.6;
    white-space: pre-wrap;
  }
}

.judgment-item:last-child {
  margin-bottom: 0;
}
</style>
